/* RESPONSIBLE TEAM: team-proactive-support */
import { tracked } from '@glimmer/tracking';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
import Coordinates from './coordinates';
import type Graph from './graph';
import type Edge from './edge';
import Bounds from './bounds';
import ConnectionPoint, { type ConnectionPointInputs } from './connection-point';

interface Inputs<DataObjectType = any> {
  position?: any;
  dataObject?: DataObjectType;
  connectionPoints?: Array<ConnectionPointInputs>;
}

export { Inputs };
export default class Node<DataObjectType = any> {
  @tracked element?: HTMLElement;
  elementRect = new DOMRect(0, 0, 0, 0);
  @tracked position: Coordinates = new Coordinates(0, 0);
  @tracked bounds: Bounds = new Bounds(0, 0, 0, 0);
  @tracked connectionPoints: Array<ConnectionPoint> = [];
  @tracked graph: Graph;
  @tracked dataObject?: DataObjectType;

  constructor(
    position: Coordinates,
    graph: Graph,
    dataObject: DataObjectType | undefined = undefined,
  ) {
    this.position = position;
    this.graph = graph;
    this.dataObject = dataObject;
  }

  addConnectionPoint(inputs: ConnectionPointInputs): ConnectionPoint {
    let { x, y, alignment, direction, dataObject } = inputs;
    let existingConnectionPoint = this._findConnectionPoint(inputs);
    if (existingConnectionPoint) {
      return existingConnectionPoint;
    }
    let connectionPoint = new ConnectionPoint(x, y, alignment, direction, this, dataObject);
    this.connectionPoints.push(connectionPoint);
    return connectionPoint;
  }

  pushConnectionPoint(inputs: ConnectionPointInputs): ConnectionPoint {
    let { x, y, alignment, direction, dataObject } = inputs;
    let connectionPoint = new ConnectionPoint(x, y, alignment, direction, this, dataObject);
    this.connectionPoints.push(connectionPoint);
    return connectionPoint;
  }

  removeConnectionPoint(connectionPoint: ConnectionPoint): void {
    let connectionPointIndex = this.connectionPoints.indexOf(connectionPoint);
    if (connectionPointIndex >= 0) {
      this.connectionPoints.splice(connectionPointIndex, 1);
    }
  }

  _findConnectionPoint(inputs: ConnectionPointInputs): ConnectionPoint | undefined {
    let { x, y, alignment, direction, dataObject } = inputs;
    return this.connectionPoints.find(
      (cp) =>
        cp.x === x &&
        cp.y === y &&
        cp.alignment === alignment &&
        cp.direction === direction &&
        (!dataObject?.edgeTypes ||
          (cp.dataObject?.edgeTypes &&
            dataObject?.edgeTypes.every((edgeType: number) =>
              cp.dataObject?.edgeTypes.includes(edgeType),
            ))),
    );
  }

  @action setElement(element: HTMLElement): void {
    this.element = element;
    this.updateElementBoundingRect(element);
  }

  @action updateElementBoundingRect(element: HTMLElement) {
    this.elementRect = element.getBoundingClientRect();
    this.calculateBounds();
  }

  @computed('graph.edges.@each.{predecessor,successor}')
  get edges(): Array<Edge> {
    return this.graph.edges.filter((edge) => edge.predecessor === this || edge.successor === this);
  }

  get inwardEdges(): Array<Edge> {
    return this.edges.filter((edge) => edge.successor === this);
  }

  get outwardEdges(): Array<Edge> {
    return this.edges.filter((edge) => edge.predecessor === this);
  }

  calculateBounds(): void {
    let nodeRect = this.elementRect;
    let top = this.position.y;
    let left = this.position.x;
    let width = (nodeRect.width || this.graph.settings.node.defaultWidth) / this.graph.state.scale;
    let height =
      (nodeRect.height || this.graph.settings.node.defaultHeight) / this.graph.state.scale;
    this.bounds.updateFromJSON({
      top,
      left,
      width,
      height,
    });
  }

  get isSelected(): boolean {
    return this.graph.state.selectedNodes.includes(this);
  }

  get isHovered(): boolean {
    return this.bounds.coversCoordinates(this.graph.state.mouseCoordinates);
  }

  get isDragging(): boolean {
    return this.graph.state.draggingNode === this;
  }

  @computed('bounds.{top,left,right,bottom}', 'graph.state.viewportBounds')
  get isWithinViewport(): boolean {
    return this.bounds.overlaps(this.graph.state.viewportBounds);
  }
}
